import { computed, onBeforeUnmount, ref, watch } from 'vue';

export const useInternalChatChannelManagement = ({
  store,
  accountId,
  selectedChannel,
  canManageChannel,
  canCreate,
  t,
}) => {
  const target = ref(null);
  let generation = 0;
  const close = () => {
    if (!target.value?.isLoading) target.value = null;
  };
  const openCreate = (mode, initialUserIds = []) => {
    generation += 1;
    target.value = {
      accountId: accountId.value,
      mode,
      initialUserIds: [...initialUserIds],
      generation,
      isLoading: false,
      error: '',
    };
  };
  const openSettings = () => {
    generation += 1;
    target.value = {
      accountId: accountId.value,
      mode: 'settings',
      channel: { ...selectedChannel.value },
      generation,
      isLoading: false,
      error: '',
    };
  };
  const valid = operation =>
    target.value === operation &&
    accountId.value === operation.accountId &&
    (operation.mode === 'settings'
      ? selectedChannel.value?.id === operation.channel.id &&
        canManageChannel.value
      : canCreate.value[operation.mode]);

  watch(
    [accountId, selectedChannel, canManageChannel, canCreate],
    () => {
      if (target.value && !valid(target.value)) target.value = null;
    },
    { flush: 'sync' }
  );
  onBeforeUnmount(() => {
    target.value = null;
  });

  const perform = async (action, payload) => {
    const operation = target.value;
    if (!operation || !valid(operation) || operation.isLoading) return false;
    operation.isLoading = true;
    operation.error = '';
    const channelId = operation.channel?.id;
    const requests = {
      create: ['createChannel', payload],
      update: ['updateChannel', { channelId, attributes: payload }],
      markUnread: ['markUnread', channelId],
      archive: ['archiveChannel', channelId],
      delete: ['deleteChannel', channelId],
      addMember: ['addMember', { channelId, attributes: payload }],
      removeMember: ['removeMember', { channelId, ...payload }],
      createCategory: ['createCategory', payload],
      updateCategory: ['updateCategory', payload],
      deleteCategory: ['deleteCategory', payload],
    };
    const [name, attributes] = requests[action];
    try {
      const result = await store.dispatch(`internalChat/${name}`, attributes);
      if (!valid(operation)) return false;
      if (['create', 'archive', 'delete'].includes(action)) target.value = null;
      if (action === 'create') {
        // Creation is already confirmed; loading the new channel cannot cause a second POST on retry.
        await store
          .dispatch('internalChat/selectChannel', result.id)
          .catch(() => {});
      }
      return true;
    } catch (error) {
      if (valid(operation))
        operation.error =
          error.response?.data?.error ||
          t('INTERNAL_CHAT.CHANNEL_ACTION_FAILED');
      return false;
    } finally {
      operation.isLoading = false;
    }
  };

  return {
    target,
    createTarget: computed(() =>
      target.value && target.value.mode !== 'settings' ? target.value : null
    ),
    settingsTarget: computed(() =>
      target.value?.mode === 'settings' ? target.value : null
    ),
    openCreate,
    openSettings,
    close,
    perform,
  };
};
